import { IMAGE_TYPE, VERIFIED_BY } from "@app/components/config";
import { APIURL } from "@app/config/apiUrl";
import { hideLoader, showLoader } from "@app/lib/features/loader/loaderSlice";
import { setMapLocation } from "@app/lib/features/properties/propertiesSlice";
import store from "@app/lib/store";
import axiosInstance from "public/middleware/axios-wrapper";

export const showFooter = (pathname: string): boolean => {
  const hideFooterRoutes = [
    "/sell",
    "/sell/dashboard",
    "/profile",
    "/my-favourites",
    "/my-messages",
    "/upload-documents",
    "/sell/my-properties",
    "/sell/pending-approval",
    "/",
    "/home",
    "/property-details-update",
    "/maintenance",
  ];
  return !hideFooterRoutes.includes(pathname);
};
export const hideHeader = (pathname: string): boolean => {
  const hideHeaderRoutes = ["/upload-documents", "/maintenance"];
  return !hideHeaderRoutes.includes(pathname);
};
export function getFilterDetailByType(type: any, data: any) {
  return data?.find((filter: any) => filter?.type === type) || [];
}

export const formatNumberWithCommas = (number: any) => {
  let x = parseInt(number)?.toFixed(0);
  return x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") || "";
};

export const getBuyPageProperties = (property: any) => {
  const thumbnail = property?.propertyMedia?.find?.((img: any) => {
    return img.name === IMAGE_TYPE.thumbnail;
  });
  const images = property?.propertyMedia?.filter?.((img: any) => {
    return img.name === IMAGE_TYPE.other;
  });
  const video = property?.propertyMedia?.find?.((img: any) => {
    return img.name === IMAGE_TYPE.videos;
  });
  const propertyName = property?.buildingMaster?.buildingName;

  return {
    propertyName: propertyName,
    propertyType: property?.propertyTypeMaster?.propertyType || "",
    propertyPrice: formatNumberWithCommas(property?.listingPrice),
    isVerified: property?.listingType === VERIFIED_BY.verifiedByVyom,
    img: thumbnail?.url || "",
    noOfBathrooms: property?.noOfBathrooms || "",
    noOfBedrooms: property?.noOfBedrooms || "",
    noOfGarages: property?.noOfGarages || "",
    totalArea: property?.totalArea || "",
    noOfParking: property?.noOfParking || "",
    video: video?.url,
    images: images,
    ...property,
  };
};

export const getSellPageProperties = (property: any) => {
  const thumbnail = property?.propertyMedia?.find?.((img: any) => {
    return img.name === IMAGE_TYPE.thumbnail;
  });
  const images = property?.propertyMedia?.filter?.((img: any) => {
    return img.name === IMAGE_TYPE.other;
  });
  const video = property?.propertyMedia?.find?.((img: any) => {
    return img.name === IMAGE_TYPE.videos;
  });
  const propertyName = property?.buildingMaster?.buildingName;
  return {
    propertyId: property?.propertyId,
    propertyName: propertyName,
    propertyType: property?.propertyTypeMaster?.propertyType || "",
    propertyPrice: formatNumberWithCommas(property?.listingPrice),
    isVerified: property?.listingType === VERIFIED_BY.verifiedByVyom,
    img: thumbnail?.url || "",
    noOfBathrooms: property?.noOfBathrooms || "",
    noOfBedrooms:
      String(property?.noOfBedrooms).toLowerCase().replace("Bedroom", "") || "",
    noOfGarages: property?.noOfGarages || "",
    totalArea: property?.totalArea || "",
    noOfParking: property?.noOfParking || "",
    video: video?.url,
    images: images,
    ...property,
  };
};

export const isArrayEmpty = (arr: any) => {
  return Array.isArray(arr) && arr.length === 0;
};

export const isObjectEmpty = (obj: any) => {
  return obj && Object.keys(obj).length === 0 && obj.constructor === Object;
};

export const getInitialName = (name: string) =>
  name
    ?.split(" ")
    ?.map((n) => n[0])
    ?.join(" ") || "";

export const getImageUrlArray = (
  data: Array<any>,
  isSmall: Boolean = false
) => {
  return (
    data?.map((item) =>
      isSmall ? item?.attributes?.formats?.medium?.url : item?.attributes?.url
    ) || []
  );
};

export const generateStrapiUrl = (
  baseUrl: string,
  queries: Array<string | any>
) => {
  const queryString = queries
    .map((param) => `populate=${encodeURIComponent(param)}`)
    .join("&");

  const url = `${baseUrl}?${queryString}`;

  return url;
};

export const arraysEqual = (arr1: any[], arr2: any[]) => {
  if (arr1.length !== arr2.length) return false;
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) return false;
  }
  return true;
};

export const debounce = <T extends (...args: any[]) => void>(
  callback: T,
  delay: number
) => {
  let timeoutId: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      callback(...args);
    }, delay);
  };
};

export const getLocation = async (payload: any) => {
  try {
    const url = `/${APIURL.PROPERTIES}/${APIURL.GET_LOCATION_DATA}`;
    const res: any = await axiosInstance.post(url, payload);
    if (res.success) {
      store.dispatch(setMapLocation(res.data));
    }
  } catch (err) {
    console.error("Error in fetching properties", err);
  }
};

const SITE_KEY = process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY;
export const executeCaptcha = async () => {
  try {
    store.dispatch(showLoader());
    const token = await window.grecaptcha.execute(SITE_KEY, {
      action: "submit",
    });
    return token;
  } catch (error) {
    console.error("reCAPTCHA error:", error);
    store.dispatch(hideLoader());
    return null;
  }
};

export const capitalizeWords = (str: string) =>
  str.replace(/\b\w/g, (char) => char.toUpperCase());


export const getTimeDifference = (date: string | Date): string => {
  const givenDate = new Date(date);

  if (isNaN(givenDate.getTime())) {
    throw new Error("Invalid date format");
  }

  const currentDate = new Date();

  const timeDifference = currentDate.getTime() - givenDate.getTime();

  const differenceInMinutes = timeDifference / (1000 * 60);
  const differenceInHours = timeDifference / (1000 * 60 * 60);
  const differenceInDays = timeDifference / (1000 * 60 * 60 * 24);

  let output: string;

  if (differenceInDays >= 1) {
    output = `${Math.floor(differenceInDays)} days ago`;
  } else if (differenceInHours >= 1) {
    output = `${Math.floor(differenceInHours)} hours ago`;
  } else if (differenceInMinutes >= 1) {
    output = `${Math.floor(differenceInMinutes)} minutes ago`;
  } else {
    output = "Just now";
  }

  return output;
};

export const pushGaEvents = (events:object ) => {
  
  window?.dataLayer?.push(events);
  // console.log(events, "events");
}